import React from 'react';
import { createPortal } from 'react-dom';
import './ColumnHeaderMenu.css';

export type ColumnHeaderOption = { value: string; label: string; count: number };
const EMPTY_FILTER_VALUE = '__column_header_menu_no_values__';

type ColumnHeaderMenuProps = {
  label: string;
  align?: 'left' | 'center' | 'right';
  sortDirection?: 'asc' | 'desc' | null;
  onSort: (direction: 'asc' | 'desc') => void;
  /** Filter values; when empty the filter section is hidden (sort-only header). */
  options?: ColumnHeaderOption[];
  /** Currently allowed values; an empty array means "all". */
  selected?: string[];
  onChangeSelected?: (values: string[]) => void;
  /** Render every value checked when an empty selection represents no filter. */
  emptyMeansAll?: boolean;
  /** Match the split sort/filter header and apply/cancel menu used by Master Inventory Requests. */
  variant?: 'combined' | 'master-inventory';
  /** Where the menu renders; pass the open `<dialog>` so the menu stays in its top layer. */
  portalTarget?: Element | null;
};

export default function ColumnHeaderMenu({
  label,
  align = 'left',
  sortDirection = null,
  onSort,
  options = [],
  selected = [],
  onChangeSelected,
  emptyMeansAll = false,
  variant = 'combined',
  portalTarget,
}: ColumnHeaderMenuProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const [search, setSearch] = React.useState('');
  const [draftSelected, setDraftSelected] = React.useState<string[]>([]);

  const filterable = options.length > 0 && Boolean(onChangeSelected);
  const effectiveSelected = React.useMemo(() => (
    emptyMeansAll && selected.length === 0
      ? options.map((option) => option.value)
      : selected.filter((value) => value !== EMPTY_FILTER_VALUE)
  ), [emptyMeansAll, options, selected]);
  const selectedSet = React.useMemo(() => new Set(effectiveSelected), [effectiveSelected]);
  const filterActive = selected.length > 0;
  const active = filterActive || sortDirection != null;
  const masterVariant = variant === 'master-inventory';

  const openMenu = React.useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const width = 260;
      const left = align === 'right'
        ? Math.max(8, rect.right - width)
        : Math.min(rect.left, window.innerWidth - width - 8);
      setPos({ top: rect.bottom + 4, left });
    }
    setSearch('');
    setDraftSelected(effectiveSelected);
    setOpen(true);
  }, [align, effectiveSelected]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Close only the menu, not a host modal dialog listening for the same key.
      event.preventDefault();
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open || !pos || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const viewportInset = 8;
    const nextTop = Math.max(
      viewportInset,
      Math.min(pos.top, window.innerHeight - rect.height - viewportInset),
    );
    // openMenu assumes 260px; the master-inventory menu is 280px and overran the right edge.
    const nextLeft = Math.max(
      viewportInset,
      Math.min(pos.left, document.documentElement.clientWidth - rect.width - viewportInset),
    );
    if (Math.abs(nextTop - pos.top) > 0.5 || Math.abs(nextLeft - pos.left) > 0.5) {
      setPos((current) => (current ? { top: nextTop, left: nextLeft } : current));
    }
  }, [open, pos]);

  const needle = search.trim().toLowerCase();
  const visibleOptions = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;
  const visibleValues = visibleOptions.map((option) => option.value);
  const menuSelectedSet = masterVariant ? new Set(draftSelected) : selectedSet;
  const allVisibleSelected = visibleValues.length > 0
    && visibleValues.every((value) => menuSelectedSet.has(value));
  const someVisibleSelected = visibleValues.some((value) => menuSelectedSet.has(value));
  // As in Excel, a typed search narrows what OK applies: only the checked values
  // among the results are kept, and values the search hides drop out of the filter.
  const visibleValueSet = new Set(visibleValues);
  const pendingSelected = needle
    ? draftSelected.filter((value) => visibleValueSet.has(value))
    : draftSelected;

  const normalizeSelectedValues = (values: string[]): string[] => {
    if (!emptyMeansAll) return values;
    if (values.length === options.length) return [];
    return values.length === 0 ? [EMPTY_FILTER_VALUE] : values;
  };

  const toggleValue = (value: string, checked: boolean) => {
    if (!onChangeSelected) return;
    const next = new Set(menuSelectedSet);
    next.delete(EMPTY_FILTER_VALUE);
    if (checked) next.add(value);
    else next.delete(value);
    const values = Array.from(next);
    if (masterVariant) setDraftSelected(values);
    else onChangeSelected(normalizeSelectedValues(values));
  };

  const toggleVisible = (checked: boolean) => {
    if (!onChangeSelected) return;
    const next = new Set(menuSelectedSet);
    next.delete(EMPTY_FILTER_VALUE);
    for (const value of visibleValues) {
      if (checked) next.add(value);
      else next.delete(value);
    }
    const values = Array.from(next);
    if (masterVariant) setDraftSelected(values);
    else onChangeSelected(normalizeSelectedValues(values));
  };

  const applyDraft = () => {
    if (pendingSelected.length === 0) return;
    onChangeSelected?.(normalizeSelectedValues(pendingSelected));
    setOpen(false);
  };

  return (
    <>
      {masterVariant ? (
        <div
          className={`column-header-menu__master-header${filterActive ? ' is-filtered' : ''}`}
          data-align={align}
        >
          <button
            type="button"
            className="column-header-menu__master-sort"
            onClick={() => onSort(sortDirection === 'asc' ? 'desc' : 'asc')}
            title="Sort ascending/descending"
          >
            <span className="column-header-menu__label">{label}</span>
            {/* Arrows, like the menu's Sort commands, so a sorted column never shows
                two ▼ beside the filter caret. */}
            <span className="column-header-menu__master-sort-mark" aria-hidden="true">
              {sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : ''}
            </span>
          </button>
          <button
            ref={buttonRef}
            type="button"
            className="column-header-menu__master-filter"
            data-active={filterActive ? 'true' : undefined}
            aria-label={filterActive ? `Filter ${label} (filtered)` : `Filter ${label}`}
            title={filterActive ? `Filter ${label} (filtered)` : `Filter ${label}`}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => (open ? setOpen(false) : openMenu())}
          >
            {/* The caret becomes a funnel once the column is filtered, as in Excel. */}
            {filterActive ? (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
                <path d="M0.5 1h9L6 5.2V9.2L4 8.2V5.2z" fill="currentColor" />
              </svg>
            ) : <span aria-hidden="true">▼</span>}
          </button>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className="column-header-menu__trigger"
          data-align={align}
          data-active={active ? 'true' : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => (open ? setOpen(false) : openMenu())}
        >
          <span className="column-header-menu__label">{label}</span>
          <span className="column-header-menu__icon" aria-hidden="true">
            {/* Unsorted reuses the sorted triangle rather than a chevron (U+2304),
                whose ink sits low in its em box and read as misaligned beside the
                label. Sorted vs unsorted is carried by opacity and colour. */}
            {sortDirection === 'asc' ? '▲' : '▼'}
            {filterActive ? <span className="column-header-menu__dot" /> : null}
          </span>
        </button>
      )}

      {open && pos && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={menuRef}
            className="column-header-menu"
            data-variant={variant}
            role="dialog"
            aria-label={`Sort and filter ${label}`}
            style={{ top: pos.top, left: pos.left }}
          >
            {masterVariant ? (
              <div className="column-header-menu__master-menu-header">
                <strong>{label}</strong>
                <span>{pendingSelected.length} of {options.length} selected</span>
              </div>
            ) : null}
            <div className="column-header-menu__commands">
              <button
                type="button"
                className="column-header-menu__command"
                onClick={() => { onSort('asc'); setOpen(false); }}
              >
                <span aria-hidden="true">↑</span> Sort A → Z
              </button>
              <button
                type="button"
                className="column-header-menu__command"
                onClick={() => { onSort('desc'); setOpen(false); }}
              >
                <span aria-hidden="true">↓</span> Sort Z → A
              </button>
              {filterable ? (
                <button
                  type="button"
                  className="column-header-menu__command"
                  disabled={!filterActive}
                  onClick={() => { onChangeSelected?.([]); setOpen(false); }}
                >
                  <span aria-hidden="true">✕</span> Clear filter
                </button>
              ) : null}
            </div>

            {filterable ? (
              <div className="column-header-menu__filter">
                <input
                  className="column-header-menu__search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter is OK, as in Excel's filter search.
                    if (event.key !== 'Enter' || !masterVariant) return;
                    event.preventDefault();
                    applyDraft();
                  }}
                  placeholder="Search values"
                  aria-label={`Search ${label} values`}
                  autoFocus
                />
                <label className="column-header-menu__option column-header-menu__option--all">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(node) => {
                      if (node) node.indeterminate = !allVisibleSelected && someVisibleSelected;
                    }}
                    disabled={!visibleValues.length}
                    onChange={(event) => toggleVisible(event.target.checked)}
                  />
                  <span>{needle ? 'Select results' : 'Select all'}</span>
                </label>
                <div className="column-header-menu__options">
                  {visibleOptions.length ? visibleOptions.map((option) => (
                    <label key={option.value} className="column-header-menu__option">
                      <input
                        type="checkbox"
                        checked={menuSelectedSet.has(option.value)}
                        onChange={(event) => toggleValue(option.value, event.target.checked)}
                      />
                      <span>{option.label}</span>
                      <small>{option.count}</small>
                    </label>
                  )) : (
                    <div className="column-header-menu__empty">No values found.</div>
                  )}
                </div>
              </div>
            ) : null}
            {masterVariant && filterable ? (
              <div className="column-header-menu__master-actions">
                <button type="button" className="btn xs" onClick={applyDraft} disabled={pendingSelected.length === 0}>OK</button>
                <button type="button" className="btn xs subtle" onClick={() => setOpen(false)}>Cancel</button>
              </div>
            ) : null}
          </div>,
          portalTarget ?? document.body,
        )
        : null}
    </>
  );
}
